import React, { useState, useEffect } from 'react';
import { getAllParticipants, getAllInvestments, getStartups, checkAuth, adminLogout } from '../services/api';
import AdminLogin from '../components/AdminLogin';

function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(null); // null = checking, true = authenticated, false = not authenticated
  const [participants, setParticipants] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Check authentication status
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await checkAuth();
      setAuthenticated(response.data.authenticated);
      if (response.data.authenticated) {
        loadData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      setAuthenticated(false);
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setAuthenticated(true);
    loadData();
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      setAuthenticated(false);
      setParticipants([]);
      setInvestments([]);
      setStartups([]);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [participantsRes, investmentsRes, startupsRes] = await Promise.all([
        getAllParticipants(),
        getAllInvestments(),
        getStartups()
      ]);
      setParticipants(participantsRes.data);
      setInvestments(investmentsRes.data);
      setStartups(startupsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        // Unauthorized - session expired
        setAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show login if not authenticated
  if (authenticated === false) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Show loading while checking authentication
  if (authenticated === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const juryMembers = participants.filter(p => p.type === 'jury');
  const audienceMembers = participants.filter(p => p.type === 'audience');

  const totalInvested = investments.reduce((sum, inv) => sum + parseFloat(inv.amount), 0);
  const juryInvested = investments
    .filter(inv => inv.participant_type === 'jury')
    .reduce((sum, inv) => sum + parseFloat(inv.amount), 0);
  const audienceInvested = investments
    .filter(inv => inv.participant_type === 'audience')
    .reduce((sum, inv) => sum + parseFloat(inv.amount), 0);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="card mb-8 bg-gradient-to-r from-gray-800 to-gray-900 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
              <p className="text-gray-300">Innovis VC Arena - Overview</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors text-white font-semibold"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'overview'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('startups')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'startups'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Startups
          </button>
          <button
            onClick={() => setActiveTab('investments')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'investments'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Investments
          </button>
          <button
            onClick={() => setActiveTab('participants')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'participants'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Participants
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Jury Members</div>
              <div className="text-3xl font-bold text-gray-900">{juryMembers.length}</div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Audience</div>
              <div className="text-3xl font-bold text-gray-900">{audienceMembers.length}</div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Startups</div>
              <div className="text-3xl font-bold text-gray-900">{startups.length}</div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Total Invested</div>
              <div className="text-3xl font-bold text-primary-600">
                {totalInvested.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Jury Invested</div>
              <div className="text-2xl font-bold text-primary-600">
                {juryInvested.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Audience Invested</div>
              <div className="text-2xl font-bold text-secondary-600">
                {audienceInvested.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Total Investments</div>
              <div className="text-3xl font-bold text-gray-900">{investments.length}</div>
            </div>
            <div className="card">
              <div className="text-sm text-gray-600 mb-1">Active Investors</div>
              <div className="text-3xl font-bold text-gray-900">
                {new Set(investments.map(inv => inv.participant_id)).size}
              </div>
            </div>
          </div>
        )}

        {/* Startups Tab */}
        {activeTab === 'startups' && (
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Startups</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Total Invested</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Investors</th>
                  </tr>
                </thead>
                <tbody>
                  {startups
                    .sort((a, b) => parseFloat(b.total_invested) - parseFloat(a.total_invested))
                    .map((startup) => (
                    <tr key={startup.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{startup.name}</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">
                        {parseFloat(startup.total_invested || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td className="py-3 px-4 text-gray-600">{startup.investor_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Investments Tab */}
        {activeTab === 'investments' && (
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">All Investments</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Participant</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Startup</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((investment) => (
                    <tr key={investment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{investment.participant_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          investment.participant_type === 'jury'
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-secondary-100 text-secondary-800'
                        }`}>
                          {investment.participant_type === 'jury' ? 'Jury' : 'Audience'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-900">{investment.startup_name}</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">
                        {parseFloat(investment.amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {new Date(investment.created_at).toLocaleString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Jury Members</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Token</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Available Budget</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Investiert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {juryMembers.map((participant) => {
                      const participantInvestments = investments.filter(inv => inv.participant_id === participant.id);
                      const invested = participantInvestments.reduce((sum, inv) => sum + parseFloat(inv.amount), 0);
                      return (
                        <tr key={participant.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{participant.name}</td>
                          <td className="py-3 px-4 text-xs font-mono text-gray-500">{participant.token.substring(0, 8)}...</td>
                          <td className="py-3 px-4 text-gray-900">
                            {parseFloat(participant.remaining_budget).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </td>
                          <td className="py-3 px-4 text-primary-600 font-semibold">
                            {invested.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Audience</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Token</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Available Budget</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Investiert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audienceMembers.map((participant) => {
                      const participantInvestments = investments.filter(inv => inv.participant_id === participant.id);
                      const invested = participantInvestments.reduce((sum, inv) => sum + parseFloat(inv.amount), 0);
                      return (
                        <tr key={participant.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{participant.name}</td>
                          <td className="py-3 px-4 text-xs font-mono text-gray-500">{participant.token.substring(0, 8)}...</td>
                          <td className="py-3 px-4 text-gray-900">
                            {parseFloat(participant.remaining_budget).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </td>
                          <td className="py-3 px-4 text-secondary-600 font-semibold">
                            {invested.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
